/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ClusterId, ProductEdition } from './cluster';
import { PriceInCents } from './currency';

export enum CloudProvider {
  AWS = 'AWS',
  GCE = 'GCE',
  AZURE = 'AZURE',
}

export type CloudProviderRegionId = string;
export type CloudProviderRegionGroupId = string;
export type PriceId = string;
export type DataRegion = { name: string; sizeInBytes: number; persistence: boolean };
export type AccessListRecord = { cidrIpv4: string; description: string | null };
export type PrivateLinkAccountId = string;
export type PrivateLinkAccount = { id: PrivateLinkAccountId };

export type PrivateLink = {
  accountIds: PrivateLinkAccountId[];
  serviceName: string | null;
};
export type PrivateLinkRequestBody = Pick<PrivateLink, 'accountIds'>;

export enum TDEType {
  AWS_KMS = 'AWS_KMS',
}

export enum NebulaClusterVersion {
  'GG8' = 'GG8',
  'GG9' = 'GG9',
}

export type TDE = {
  type: TDEType;
  cmkArn: string;
  roleArn: string;
  externalId: string;
};

export type NebulaClusterDeploymentRequestBody = {
  version: NebulaClusterVersion;
  cloudProviderRegion: CloudProviderRegionId;
  clusterSize: NebulaClusterSize;
  nodeCount: number;
  igniteDataRegions: DataRegion[];
  rules: AccessListRecord[] | null;
  userLogin: string;
  userPassword: string;
  /** [CC-3698](https://ggsystems.atlassian.net/browse/CC-3698) A temporary nebula cluster will be created */
  temporary: boolean;
  privateLink: PrivateLinkRequestBody | null;
  tde: TDE | null;
  azEnabled: boolean;
  clusterName: string | null;
};

export type NebulaClusterDeploymentResponseBody = {
  clusterId: ClusterId;
  connectionInfo: [];
};

export type NebulaClusterAccessListRequestBody = {
  rules: AccessListRecord[];
};

export type NebulaClusterAccessListResponseBody = {
  rules: AccessListRecord[];
};

export enum NebulaClusterSize {
  S = 'S',
  M = 'M',
  L = 'L',
}

export type NebulaPrice = {
  priceId: PriceId;
  clusterSize: NebulaClusterSize;
  name: string;
  price: PriceInCents;
  description: string;
  mainInfo: string;
  advancedInfo: string;
  cloudProvider: CloudProvider;
  totalMemoryInBytes: number;
  /**
   * Node CPU core count
   */
  cpu: number;
  /**
   * Node RAM size, in bytes
   */
  ramTotalSize: number;
  /**
   * Node disk storage size, in bytes
   */
  diskStorageSize: number;
};

type NebulaClusterBaseInfo = {
  cloudProviderRegion: CloudProviderRegionId;
  priceId: PriceId;
  nodeCount: number;
  createdAt: number;
  endOfLifeAt?: number;
  totalPrice: PriceInCents;
  /**
   * Human-readable product name, i.e. "Small instance". Available for all statuses.
   */
  productName: string;
  privateLink: PrivateLink | null;
  status: NebulaClusterStatus;
  azEnabled: boolean;
  hasInMemoryDataRegions: boolean;
};

interface NebulaClusterAdditionalInfo {
  connectionInfo: {
    urls: string[] | null;
    // For ClusterStatus.Failed = null;
  };
  startedAt: number | null;
  // For ClusterStatus.PROVISIONING or ClusterStatus.RESUMING = null;
  destroyStartedAt: number | null;
  // For Cluster.DESTROYED = number;
}

export type NebulaClusterInfo = NebulaClusterBaseInfo & NebulaClusterAdditionalInfo;

export type NebulaClusterConnectionInfoResponse = NebulaClusterInfo['connectionInfo'];

export enum NebulaClusterStatus {
  PROVISIONING = 'PROVISIONING',
  RUNNING = 'RUNNING',
  STOPPING = 'STOPPING',
  STOPPED = 'STOPPED',
  RESUMING = 'RESUMING',
  DESTROYING = 'DESTROYING',
  DESTROYED = 'DESTROYED',
  FAILED = 'FAILED',
  UPDATING = 'UPDATING',
  SUSPENDING = 'SUSPENDING',
  SUSPENDED = 'SUSPENDED',
}

export enum NebulaSubscriptionStatus {
  ACTIVE = 'ACTIVE',
  UNPAID = 'UNPAID',
  NOT_PURCHASED = 'NOT_PURCHASED',
}

export enum HostedType {
  ATTACHED = 'ATTACHED',
  MANAGED = 'MANAGED',
}

export type NebulaClusterSubscription = {
  id: ClusterId;
  tag: string;
  productName: string;
  nodesCount: number;
  price: PriceInCents;
} & (
  | {
      hostedType: HostedType.ATTACHED;
      instanceType: ProductEdition | null;
    }
  | {
      hostedType: HostedType.MANAGED;
      instanceType: string | null;
    }
);

export type NebulaSubscriptionInfo = {
  availableCredit: PriceInCents;
} & (
  | {
      status: NebulaSubscriptionStatus.NOT_PURCHASED;
    }
  | ({
      usage: {
        used: PriceInCents;
        creditsDiscount: PriceInCents;
        couponDiscount: PriceInCents;
        total: PriceInCents;
      };
      nextPaymentTime: number;
      clusters: NebulaClusterSubscription[];
    } & (
      | {
          status: NebulaSubscriptionStatus.ACTIVE;
          // balanceExhaustionTime is null for subscription with attached credit card.
          balanceExhaustionTime: number | null;
        }
      | {
          status: NebulaSubscriptionStatus.UNPAID;
          clusterTerminationTime: number;
        }
    ))
);

export type CloudProviderRegion = {
  id: CloudProviderRegionId;
  groupId: CloudProviderRegionGroupId;
  description: string;
};
export type CloudProviderRegionFrontend = CloudProviderRegion & {
  name: string;
};
export type NebulaRegionList = {
  cloudProvider: CloudProvider;
  regions: CloudProviderRegion[];
}[];

export type CloudProviderAccount = {
  cloudProvider: CloudProvider;
  accountId: string;
};
export type NebulaCloudProviderAccountListResponseBody = CloudProviderAccount[];

export type ToggleClusterSuspendedStateRequestBody = { suspended: boolean };
